import { isNode } from 'client/utils/environment';
import { RequestContextClient } from './request-context-client';

const clientContext = new RequestContextClient();

/**
 * Returns the request context based on the environment.
 * In a Node.js environment, it's using `AsyncLocalStorage` instance from `server.js`.
 * In a client-side environment, we are initializing context.
 * @returns {AsyncLocalStorage | RequestContextClient} The initialized context store.
 */
function getContext() {
  return isNode() ? global.AsyncLocalStorage : clientContext;
}

function getStore() {
  const context = getContext();
  return context?.getStore?.();
}

function isObject(o) {
  return typeof o === 'object' && o !== null;
}

function setSendToClient(key, sendToClient) {
  if (sendToClient === undefined) {
    /**
     * prevents the following:
     * requestContext.update('loggerMetadata', { something: '' }, { sendToClient: true });
     * requestContext.update('loggerMetadata', { something: '' }); // should NOT change setToClient to false
     */
    return;
  }
  const store = getStore();
  if (sendToClient) {
    store.sendToClientKeys.add(key);
  } else {
    store.sendToClientKeys.delete(key);
  }
}

/**
 * isomorphic singleton util
 * Provides methods to `get(key)`, `set(key, value)`, and `update(key, value) within the request context.
 */
export const requestContext = {
  /**
   * @example
   * const { loggerMetadata } = requestContext.get();
   * const { isBot } = loggerMetadata;
   *
   * const { isBot } = requestContext.get('loggerMetadata');
   * const isBot = requestContext.get('loggerMetadata.isBot');
   *
   * @param {string|number|undefined} key
   * @returns {*}
   */
  get(key) {
    const store = getStore();
    return key ? store?.[key] : (store ?? {});
  },

  /**
   * set(key, value, options)
   *
   * adds a key/value pair to the request context if one does not already exist.
   * if one already exists, set() will replace it.
   *
   * as a convention, use set()
   * - for storing functions, instances of a class/promise
   * - to reset a previously set array/object (e.g. to empty array/object)
   *
   * @examples
   * requestContext.set('visitorId', visitorId)
   * requestContext.set('reduxStore', reduxStore); // if store.reduxStore already exists, it will be replaced with the new value
   * requestContext.set('promiseCache.vehicle.makes.honda.models', p);
   *
   * @param {string|number} key
   * @param {*} value
   * @param {true|false|undefined} options.sendToClient not specifying would leave the previously set value untouched.
   * if specified, true will send value to the client side, false will not. only supported if value is an object of
   * key/val pairs.
   */
  set(key, value, { sendToClient } = {}) {
    const store = getStore();

    if (!store) return;

    store[key] = value;
    setSendToClient(key, sendToClient);
  },

  /**
   * update(key, value, options)
   *
   * adds a key/value pair to the request context if one does not already exist.
   * If one already exists, and the existing value is an object, update() will merge props from value param into existing.
   *
   * as a convention use update()
   * - for storing objects that are meant to be key/value pairs (e.g. loggerMetadata), update() will be handy for incremental updates
   *
   * @example
   * // add visitorId to existing loggerMetadata. create loggerMetadata if it does not already exist
   * // pass sendToClient: true to send loggerMetadata to client side - i.e. calling requestContext.get('loggerMetadata') on the client will return whatever was set on the server
   * requestContext.update('loggerMetadata', { visitorId }, { sendToClient: true });
   * // add x_venom_test to existing loggerMetadata. no need to specify sendToClient:true again
   * requestContext.update('loggerMetadata', { x_venom_test });
   *
   * @param {string|number} key
   * @param {*} value
   * @param {true|false|undefined} options.sendToClient not specifying would leave the previously set value untouched.
   * if specified, true will send value to the client side, false will not. only supported if value is an object of
   * key/val pairs.
   */
  update(key, value, { sendToClient } = {}) {
    const store = getStore();

    if (!store) return;

    const existingValue = store[key];
    if (isObject(existingValue)) {
      // e.g. requestContext.update('loggerMetadata', { prop: 'val' }) and store.loggerMetadata already exists
      // copy props from the new object into the existing object
      if (Array.isArray(existingValue)) {
        store[key] = [...existingValue, ...value];
      } else {
        store[key] = { ...existingValue, ...value };
      }
      setSendToClient(key, sendToClient);
      return;
    }
    // no existing value or existing value is of scalar type (e.g. string, number, bool)
    // e.g. update('loggerMetadata', { prop: 'val' }) and store.loggerMetadata does not exist
    // in these cases update() should fallback to set()
    this.set(key, value, { sendToClient });
  },

  /**
   * wrapper for https://nodejs.org/api/async_context.html#asynclocalstoragerunstore-callback-args
   * called from middleware to initialize the store for each request
   */
  run(store, callback) {
    // eslint-disable-next-line no-param-reassign
    store.sendToClientKeys = new Set();
    const context = getContext();
    return context.run(store, callback);
  },

  /**
   * returns all data set on the server with sendToClient: true
   * @returns {{}}
   */
  getClientData() {
    const store = getStore();

    if (!store) return {};

    const clientData = {};
    store.sendToClientKeys.forEach(key => {
      clientData[key] = store[key];
    });
    return clientData;
  },
};

global.EDM = global.EDM || {};
global.EDM.Venom = global.EDM.Venom || {};
global.EDM.Venom.debug = global.EDM.Venom.debug || {};

global.EDM.Venom.debug.requestContext = requestContext;
